<script setup lang="ts">
import type { LogoItem } from '~/constants/brands'
import type { BrandCard } from '~/composables/useBrandShop'
import { prepareLink } from '~/utils/images'

const props = withDefaults(defineProps<{
  showHeader?: boolean
  showSeeAll?: boolean
  title?: string
  titlePrefix?: string
  titleHighlight?: string
  titleSuffix?: string
  compactFooter?: boolean
  category?: string
  items?: LogoItem[]
}>(), {
  showHeader: true,
  showSeeAll: true,
  compactFooter: false,
})

const localePath = useLocalePath()
const api = useApi()

const { data } = await useAsyncData(
  () => `brands-banner-${props.category || 'mixed'}-${props.items ? 'fixed' : 'live'}`,
  async () => {
    if (props.items) return null
    const query = props.category ? `?category=${encodeURIComponent(props.category)}` : ''
    try {
      return await api<{ brands: BrandCard[] }>(`/Public/Brands${query}`)
    } catch (error) {
      console.error('Failed to load brand carousel', error)
      return { brands: [] }
    }
  },
)

const logos = computed<LogoItem[]>(() => {
  if (props.items) return props.items
  return (data.value?.brands ?? []).map(brand => ({
    slug: brand.slug,
    src: brand.logoUrl ? prepareLink(brand.logoUrl) : undefined,
    alt: brand.name,
  }))
})

const seeAllTarget = computed(() =>
  props.category ? `/brands#${props.category}` : '/brands',
)
</script>

<template>
  <section v-if="logos.length" id="brands" :class="{ compact: !showHeader, compactFooter }">
    <template v-if="showHeader">
      <page-sub-title
        :subtitle="title ?? $t('brandsBannerTitle')"
        :subtitle-prefix="titlePrefix"
        :subtitle-highlight="titleHighlight"
        :subtitle-suffix="titleSuffix"
      />
    </template>
    <NuxtLink v-if="showSeeAll" class="btn-cta center-btn mt-1" :to="localePath(seeAllTarget)">{{ $t('seeAll') }}</NuxtLink>
    <LogoCarousel :items="logos" :compact="!showHeader" />
  </section>
</template>

<style scoped>
#brands {
  background-image: url("/images/background/drops-xl.avif");
  background-position: top center;
  padding-bottom: 6rem;
  padding-top: 6rem;

  &.compact {
    padding-top: 4rem;
    padding-bottom: 3rem;
  }

  &.compactFooter {
    padding-bottom: 2rem;
  }
}

section::-webkit-scrollbar {
  display: none;
}
</style>
